import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useTheme } from '@/theme/useTheme';

interface CommunityCard {
  platform: string;
  handle: string;
  description: string;
  icon: string;
  bg: string;
  btnLabel: string;
  href: string;
}

const AUTO_DELAY = 3000;
const CARD_W     = 280;
const SIDE_OFF   = 308;

const CommunitySection = () => {
  const { t } = useTranslation();
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  const socialLinksRaw = t('home.community.cards', { returnObjects: true });
  const socialLinks = Array.isArray(socialLinksRaw)
    ? (socialLinksRaw as CommunityCard[])
    : [];
  const TOTAL = socialLinks.length;
  const [activeIdx, setActiveIdx] = useState(0);
  const autoRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const stopAuto = () => { if (autoRef.current) clearInterval(autoRef.current); };

  const startAuto = useCallback(() => {
    stopAuto();
    autoRef.current = setInterval(() => {
      setActiveIdx((prev) => (prev + 1) % TOTAL);
    }, AUTO_DELAY);
  }, [TOTAL]);

  useEffect(() => {
    startAuto();
    return stopAuto;
  }, [startAuto]);

  const goNext = () => { stopAuto(); setActiveIdx((p) => (p + 1) % TOTAL);           startAuto(); };
  const goPrev = () => { stopAuto(); setActiveIdx((p) => (p - 1 + TOTAL) % TOTAL);   startAuto(); };
  const goTo   = (i: number) => { stopAuto(); setActiveIdx(i); startAuto(); };

  const getOffset = (i: number) => {
    let diff = (i - activeIdx + TOTAL) % TOTAL;
    if (diff > TOTAL / 2) diff -= TOTAL;
    return diff;
  };

  return (
    <section
      id="comunidade"
      className="w-full py-20 sm:py-28 px-4 overflow-hidden"
      style={{ background: isDark ? 'linear-gradient(160deg, #081424 0%, #06111f 60%, #0d1b31 100%)' : 'linear-gradient(160deg, #F0F7FF 0%, #F8FAFF 60%, #FFF0F3 100%)' }}
    >
      <div className="max-w-7xl mx-auto">

        {/* Header */}
        <div className="text-center mb-12 sm:mb-14">
          <div className="inline-flex items-center gap-2 text-xs font-semibold px-3 py-1.5 rounded-full mb-5 tracking-widest uppercase border" style={{ background: 'var(--surface-primary)', color: 'var(--text-primary)', borderColor: 'var(--border-soft)' }}>
            <div className="w-4 h-4 flex items-center justify-center">
              <i className="ri-community-line text-[#E8365D]" />
            </div>
            {t('home.community.badge')}
          </div>
          <h2
            className="text-3xl sm:text-4xl lg:text-5xl font-black text-[#091D42] mb-4 leading-tight"
            style={{ fontFamily: "'Space Grotesk', sans-serif", fontWeight: 900 }}
          >
            {t('home.community.title')}
          </h2>
          <p className="text-base sm:text-lg max-w-xl mx-auto" style={{ fontFamily: "'Inter', sans-serif", color: 'var(--text-muted)' }}>
            {t('home.community.description')}
          </p>
        </div>

        {/* 3-card carousel */}
        <div className="relative flex items-center justify-center">
          {/* Prev */}
          <button
            onClick={goPrev}
            className="absolute left-0 sm:left-4 z-20 w-10 h-10 rounded-full flex items-center justify-center cursor-pointer whitespace-nowrap transition-all duration-200"
            style={{ background: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(9,29,66,0.07)', border: `1px solid ${isDark ? 'rgba(255,255,255,0.12)' : 'rgba(9,29,66,0.12)'}`, color: 'var(--text-primary)' }}
          >
            <div className="w-5 h-5 flex items-center justify-center">
              <i className="ri-arrow-left-s-line text-xl" />
            </div>
          </button>

          {/* Stage */}
          <div
            className="relative select-none"
            style={{ height: '360px', width: '100%', maxWidth: '960px', overflow: 'hidden' }}
          >
            <div className="absolute inset-0 flex items-center justify-center">
              {socialLinks.map((social, i) => {
                const offset   = getOffset(i);
                const isActive = offset === 0;
                const visible  = Math.abs(offset) <= 1;
                return (
                  <div
                    key={i}
                    onClick={() => !isActive && goTo(i)}
                    style={{
                      position:      'absolute',
                      width:         `${CARD_W}px`,
                      height:        '300px',
                      transform:     `translateX(${offset * SIDE_OFF}px) scale(${isActive ? 1 : 0.84})`,
                      opacity:       visible ? (isActive ? 1 : 0.58) : 0,
                      transition:    'transform 0.55s cubic-bezier(0.4,0,0.2,1), opacity 0.55s ease',
                      zIndex:        isActive ? 10 : visible ? 5 : 0,
                      pointerEvents: visible ? 'auto' : 'none',
                      cursor:        isActive ? 'default' : 'pointer',
                    }}
                  >
                    <div
                      className="w-full h-full rounded-2xl flex flex-col p-6"
                      style={{
                        background: isActive ? '#091D42' : 'var(--surface-primary)',
                        border:     isActive ? 'none' : '1px solid var(--border-soft)',
                        boxShadow:  isActive
                          ? '0 24px 64px rgba(0,196,240,0.18)'
                          : '0 4px 24px rgba(9,29,66,0.07)',
                      }}
                    >
                      {/* top accent bar */}
                      <div
                        className="h-0.5 w-full rounded-full mb-4"
                        style={{ background: isActive ? 'linear-gradient(90deg, #00C4F0, transparent)' : 'transparent' }}
                      />

                      <div className="flex items-start justify-between mb-3">
                        <div
                          className="w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0"
                          style={{ background: isActive ? 'rgba(0,196,240,0.15)' : 'var(--surface-tertiary)' }}
                        >
                          <div className="w-6 h-6 flex items-center justify-center">
                            <i className={`${social.icon} text-xl`} style={{ color: '#00C4F0' }} />
                          </div>
                        </div>
                        <span
                          className="text-xs font-bold uppercase tracking-widest"
                          style={{ color: isActive ? 'rgba(255,255,255,0.5)' : 'var(--text-muted)' }}
                        >
                          {social.platform}
                        </span>
                      </div>

                      <p
                        className="font-bold text-sm mb-1 leading-snug flex-shrink-0"
                        style={{ color: isActive ? 'white' : 'var(--text-primary)' }}
                      >
                        {social.handle}
                      </p>

                      <p
                        className="text-xs leading-relaxed mb-4 flex-1"
                        style={{ color: isActive ? 'rgba(255,255,255,0.55)' : 'var(--text-muted)' }}
                      >
                        {social.description}
                      </p>

                      <a
                        href={social.href}
                        target="_blank"
                        rel="nofollow noopener noreferrer"
                        className="mt-auto inline-flex items-center gap-2 text-sm font-semibold cursor-pointer whitespace-nowrap transition-colors duration-200"
                        style={{ color: '#00C4F0' }}
                        onClick={e => e.stopPropagation()}
                      >
                        <div className="w-4 h-4 flex items-center justify-center">
                          <i className={`${social.icon} text-sm`} />
                        </div>
                        {social.btnLabel}
                      </a>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Next */}
          <button
            onClick={goNext}
            className="absolute right-0 sm:right-4 z-20 w-10 h-10 rounded-full flex items-center justify-center cursor-pointer whitespace-nowrap transition-all duration-200"
            style={{ background: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(9,29,66,0.07)', border: `1px solid ${isDark ? 'rgba(255,255,255,0.12)' : 'rgba(9,29,66,0.12)'}`, color: 'var(--text-primary)' }}
          >
            <div className="w-5 h-5 flex items-center justify-center">
              <i className="ri-arrow-right-s-line text-xl" />
            </div>
          </button>
        </div>

        {/* Dots */}
        <div className="flex items-center justify-center gap-2 mt-6">
          {socialLinks.map((_, i) => (
            <button
              key={i}
              onClick={() => goTo(i)}
              className="cursor-pointer transition-all duration-300 rounded-full"
              style={{
                width:      i === activeIdx ? '24px' : '8px',
                height:     '8px',
                background: i === activeIdx ? '#E8365D' : 'rgba(9,29,66,0.2)',
              }}
            />
          ))}
        </div>

      </div>
    </section>
  );
};

export default CommunitySection;
